/* eslint-disable camelcase */
import type { FC, PropsWithChildren } from 'react'
import React from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import Button from '@segment/matcha/components/Button'
import Typography from '@segment/matcha/components/Typography'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import SignupButton from '@segment/matcha/recipes/SignupButton'

import PlanImage from './PlanImage'

const copy = {
  subtitle: `Free every month`,
  heading: `Twilio Developer Plan`,
  description: `Activate audiences in ${process.env.INTEGRATION_COUNT}+ marketing and analytics tools in real time.`,
  details: `* All other details are similar to Segment’s Team plan.`,
}

const Plan: FC<PropsWithChildren<unknown>> = () => {
  const theme = useTheme()

  return (
    <Wrapper>
      <SectionBase variant="2Col" reverse={true}>
        <SectionCopy>
          <CopyTextBase>
            <CopyText.Overline>{copy.subtitle}</CopyText.Overline>
            <CopyText.Heading variant="h2" tag="h2">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description>{copy.description}</CopyText.Description>
          </CopyTextBase>
          <ButtonsWrapper>
            <SignupButton additionalParams={{ twilio_developer_offer: `1` }}>Sign up</SignupButton>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              href={`https://segment.com/docs/guides/usage-and-billing/twilio-developer-plan/`}
            >
              View documentation
            </Button>
          </ButtonsWrapper>

          <List>
            <ListItem>
              <TypographyItem variant="h5" tag="p">
                3 Sources
                <Typography variant="h6" tag="span">
                  &nbsp;(+1 from standard free plan)
                </Typography>
              </TypographyItem>
            </ListItem>
            <ListItem>
              <TypographyItem variant="h5" tag="p">
                1 Warehouse Destination
              </TypographyItem>
            </ListItem>
            <ListItem>
              <TypographyItem variant="h5" tag="p">
                10,000 MTUs
                <Typography variant="h6" tag="span">
                  &nbsp;(+9,000 from standard free plan)
                </Typography>
              </TypographyItem>
            </ListItem>
            <ListItem>
              <TypographyItem variant="h5" tag="p">
                10 seats
              </TypographyItem>
            </ListItem>
          </List>

          <Typography variant="pMedium" tokens={{ color: theme.palette.twilio.gray[80] }}>
            {copy.details}
          </Typography>
        </SectionCopy>

        <Section.Contents>
          <PlanImage />
        </Section.Contents>
      </SectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const SectionCopy = styled(Section.Copy)`
  direction: ltr;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 440px;
  }
`

const List = styled.ul`
  margin-top: 40px;
  padding-left: 30px;
`

const ListItem = styled.li``

const TypographyItem = styled(Typography)`
  margin: 0;
`

const ButtonsWrapper = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 16px;
`

export default Plan
